import { faPaperclip, faStop, faPaperPlaneTop } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormEvent, forwardRef } from "react";
import ReactTextareaAutosize from "react-textarea-autosize";
import { toast } from "react-toastify";
import useFilePicker from "app/modules/aiAssistant/conversation/input/hooks/useFilePicker";
import { ChatDocument } from "app/modules/aiAssistant/AIAssistantSlice";
import DocumentChips from "app/modules/aiAssistant/conversation/components/DocumentChips";
import "app/modules/aiAssistant/styles/shared.scss";
import "./ChatInput.scss";

const DEFAULT_ACCEPTED_FILE_TYPES = ["pdf", "docx", "txt"];
const NO_DOCUMENTS: ChatDocument[] = [];
// Matches the API's upload limit
const MAXIMUM_FILE_SIZE_BYTES = 20 * 1024 * 1024;

export type ChatInputTestIds = {
  messageInput: string;
  sendButton: string;
  attachButton?: string;
  fileInput?: string;
  getRemoveDocumentButton?: (documentID: string) => string;
};

export type ChatInputAttachments = {
  documents: ChatDocument[];
  uploadingDocumentIDs?: string[];
  filesLeftToAttach: number;
  acceptedFileTypes?: string[];
  onFilesAdded: (files: File[]) => void;
  onFileRemoved: (document: ChatDocument) => void;
};

type Props = {
  testIds: ChatInputTestIds;
  onSend: (text: string) => void;
  stopReply?: () => void;
  draft: string;
  setDraft: React.Dispatch<React.SetStateAction<string>>;
  isAwaitingReply: boolean;
  sendDisabled?: boolean;
  placeholder?: string;
  disabled?: boolean;
  // Without it, the input has no attach button
  attachments?: ChatInputAttachments;
};

const ChatInput = forwardRef<HTMLTextAreaElement, Props>(function ChatInput(
  {
    testIds,
    onSend,
    stopReply,
    draft,
    setDraft,
    isAwaitingReply,
    sendDisabled = false,
    placeholder = "Ask Assistimo a question...",
    disabled = false,
    attachments,
  },
  inputRef
) {
  const documents = attachments?.documents ?? NO_DOCUMENTS;
  const acceptedFileTypes = attachments?.acceptedFileTypes ?? DEFAULT_ACCEPTED_FILE_TYPES;
  const filesLeftToAttach = attachments?.filesLeftToAttach ?? 0;
  const attachDisabled = disabled || isAwaitingReply || filesLeftToAttach === 0;

  const {
    fileInputRef,
    isDragActive,
    openFileDialog,
    handleDragEnter,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleChange,
  } = useFilePicker({
    files: documents,
    disabled: attachDisabled,
    acceptedFileTypes,
    filesLeftToAttach,
    maximumFileSizeBytes: MAXIMUM_FILE_SIZE_BYTES,
    displayError: (error) => {
      toast.error(error);
    },
    onFilesAdded: attachments?.onFilesAdded,
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (isAwaitingReply || sendDisabled) return;

    setDraft("");
    onSend(draft);
  };

  return (
    <form
      className={`assistimo-composer ${isDragActive && !attachDisabled ? "assistimo-composer--drag-active" : ""}`}
      onSubmit={submit}
      // Always handled, so a file dropped where attaching is off isn't opened by the browser
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <DocumentChips
        documents={documents}
        uploadingDocumentIDs={attachments?.uploadingDocumentIDs}
        onRemove={attachments?.onFileRemoved}
        getRemoveButtonTestId={testIds.getRemoveDocumentButton}
      />
      {attachments && (
        <input
          data-test-id={testIds.fileInput}
          type="file"
          hidden
          accept={toAcceptAttribute(acceptedFileTypes)}
          ref={fileInputRef}
          disabled={attachDisabled}
          onChange={handleChange}
        ></input>
      )}
      <ReactTextareaAutosize
        data-test-id={testIds.messageInput}
        className="assistimo-composer__input"
        aria-label="Message Assistimo"
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        ref={inputRef}
        disabled={disabled}
        onKeyDown={(e) => {
          // Enter also confirms a word typed with an input method editor, e.g. for Japanese
          if (e.key !== "Enter" || e.shiftKey || e.nativeEvent.isComposing) return;

          e.preventDefault();
          if (!isAwaitingReply && !sendDisabled) e.currentTarget.form?.requestSubmit();
        }}
        minRows={1}
        maxRows={4}
        value={draft}
      ></ReactTextareaAutosize>
      <div className="assistimo-composer__footer">
        <div className="assistimo-composer__tools">
          {attachments && (
            <button
              data-test-id={testIds.attachButton}
              type="button"
              className="assistimo-icon-button assistimo-icon-button--filled"
              aria-label="Attach a file"
              title="Attach a file"
              disabled={attachDisabled}
              onClick={openFileDialog}
            >
              <FontAwesomeIcon icon={faPaperclip} />
            </button>
          )}
          <span className="assistimo-composer__hint">Enter to send · Shift + Enter for a new line</span>
        </div>
        <button
          data-test-id={testIds.sendButton}
          className="assistimo-composer__send-button"
          type={isAwaitingReply ? "button" : "submit"}
          aria-label={isAwaitingReply ? "Stop" : "Send"}
          title={isAwaitingReply ? "Stop" : "Send"}
          onClick={isAwaitingReply ? stopReply : undefined}
          disabled={disabled || (isAwaitingReply ? stopReply === undefined : sendDisabled)}
        >
          <FontAwesomeIcon icon={isAwaitingReply ? faStop : faPaperPlaneTop} />
        </button>
      </div>
    </form>
  );
});

// File types are extensions or MIME types, and "*" allows anything
const toAcceptAttribute = (fileTypes: string[]) =>
  fileTypes.includes("*") ? undefined : fileTypes.map((type) => (type.includes("/") ? type : `.${type}`)).join(",");

export default ChatInput;
